/**
 * PDP Reviews Injector
 *
 * Replaces the Liquid-rendered review cards in the reviews-carousel section
 * with content-map-driven reviews using a fallback chain:
 *   1. collections[occasion].reviews  (if ?occasion= present)
 *   2. products[handle].reviews       (product-specific)
 *   3. defaults.reviews               (global fallback)
 *
 * Keeps the existing Liquid reviews as a server-side / SEO fallback —
 * this script replaces them after DOMContentLoaded.
 */
(function () {
  'use strict';

  /* ── SVG templates ─────────────────────────────────────────── */

  var STAR_SVG =
    '<svg class="review-card__star review-card__star--filled" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5">' +
    '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>' +
    '</svg>';

  var AVATAR_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>' +
    '<circle cx="12" cy="7" r="4"></circle>' +
    '</svg>';

  var VERIFIED_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">' +
    '<path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-.997-6l7.07-7.071-1.414-1.414-5.656 5.657-2.829-2.829-1.414 1.414L11.003 16z"/>' +
    '</svg>';

  /* ── Helpers ────────────────────────────────────────────────── */

  function getOccasion() {
    try { return new URLSearchParams(window.location.search).get('occasion'); }
    catch (e) { return null; }
  }

  /**
   * Resolve reviews data using fallback chain
   * @returns {{ heading: string, subheading: string, items: Array }} | null
   */
  function resolveReviews(map, productHandle, occasion) {
    /* 1. Occasion reviews */
    if (occasion && map.collections && map.collections[occasion] && map.collections[occasion].reviews) {
      var occ = map.collections[occasion].reviews;
      if (occ.items && occ.items.length) return occ;
    }

    /* 2. Product reviews */
    if (productHandle && map.products && map.products[productHandle] && map.products[productHandle].reviews) {
      var prod = map.products[productHandle].reviews;
      if (prod.items && prod.items.length) return prod;
    }

    /* 3. Default reviews */
    if (map.defaults && map.defaults.reviews) {
      return map.defaults.reviews;
    }

    return null;
  }

  /**
   * Build a single review card slide element
   */
  function buildCard(item, index, total, sectionId, cardColorScheme) {
    var slide = document.createElement('div');
    slide.className = 'reviews-carousel__slide slideshow__slide';
    slide.id = 'Slide-' + sectionId + '-' + (index + 1);
    slide.setAttribute('role', 'group');
    slide.setAttribute('aria-roledescription', 'Slide');
    slide.setAttribute('aria-label', (index + 1) + ' of ' + total);
    slide.setAttribute('data-review-item', String(index));

    var card = document.createElement('div');
    card.className = 'review-card color-' + cardColorScheme;

    /* Stars */
    var stars = document.createElement('div');
    stars.className = 'review-card__stars';
    stars.setAttribute('aria-label', (item.rating || 5) + ' out of 5 stars');
    var starCount = item.rating || 5;
    for (var s = 0; s < starCount; s++) {
      stars.innerHTML += STAR_SVG;
    }
    card.appendChild(stars);

    /* Quote */
    var quote = document.createElement('blockquote');
    quote.className = 'review-card__text';
    quote.textContent = '\u201C' + item.text + '\u201D';
    card.appendChild(quote);

    /* Author */
    var author = document.createElement('div');
    author.className = 'review-card__author';

    var avatar = document.createElement('div');
    avatar.className = 'review-card__avatar review-card__avatar--placeholder';
    avatar.innerHTML = AVATAR_SVG;
    author.appendChild(avatar);

    var info = document.createElement('div');
    info.className = 'review-card__author-info';

    if (item.name) {
      var nameEl = document.createElement('span');
      nameEl.className = 'review-card__name';
      nameEl.textContent = item.name;
      info.appendChild(nameEl);
    }

    var verified = document.createElement('span');
    verified.className = 'review-card__verified';
    verified.innerHTML = VERIFIED_SVG + ' Verified Buyer';
    info.appendChild(verified);

    author.appendChild(info);
    card.appendChild(author);

    /* Product purchased */
    if (item.product) {
      var prodLine = document.createElement('p');
      prodLine.className = 'review-card__product';
      prodLine.textContent = 'Purchased: ' + item.product;
      card.appendChild(prodLine);
    }

    slide.appendChild(card);
    return slide;
  }

  /**
   * Build dot navigation
   */
  function buildDots(count, sectionId) {
    var dotsWrapper = document.createElement('div');
    dotsWrapper.className = 'reviews-carousel__dots slideshow__control-wrapper';
    for (var i = 0; i < count; i++) {
      var btn = document.createElement('button');
      btn.className = 'slider-counter__link slider-counter__link--dots link';
      btn.setAttribute('aria-label', 'Load slide ' + (i + 1) + ' of ' + count);
      btn.setAttribute('aria-controls', 'Slider-' + sectionId);
      var dot = document.createElement('span');
      dot.className = 'dot';
      btn.appendChild(dot);
      dotsWrapper.appendChild(btn);
    }
    return dotsWrapper;
  }

  /**
   * Rebuild all review cards and controls inside the section
   */
  function rebuildReviews(section, reviews) {
    if (!section || !reviews || !reviews.items || !reviews.items.length) return;

    /* Update heading & subheading */
    var headingEl = section.querySelector('[data-reviews-heading]');
    var subheadingEl = section.querySelector('[data-reviews-subheading]');
    if (headingEl && reviews.heading) headingEl.innerHTML = reviews.heading;
    if (subheadingEl && reviews.subheading) subheadingEl.textContent = reviews.subheading;

    /* Get section ID and card color scheme */
    var sectionId = section.id || '';
    /* Extract section ID from the Slider container */
    var sliderContainer = section.querySelector('[data-reviews-container]');
    if (sliderContainer && sliderContainer.id) {
      sectionId = sliderContainer.id.replace('Slider-', '');
    }

    /* Detect card color scheme from existing cards */
    var existingCard = section.querySelector('.review-card');
    var cardColorScheme = 'scheme-1';
    if (existingCard) {
      var classList = existingCard.className.split(/\s+/);
      for (var c = 0; c < classList.length; c++) {
        if (classList[c].indexOf('color-') === 0) {
          cardColorScheme = classList[c].replace('color-', '');
          break;
        }
      }
    }

    /* Rebuild slides */
    if (!sliderContainer) return;
    sliderContainer.innerHTML = '';
    var items = reviews.items;
    for (var i = 0; i < items.length; i++) {
      sliderContainer.appendChild(buildCard(items[i], i, items.length, sectionId, cardColorScheme));
    }

    /* Rebuild dot navigation */
    var controlsEl = section.querySelector('.reviews-carousel__controls');
    if (controlsEl) {
      var oldDots = controlsEl.querySelector('.reviews-carousel__dots');
      var newDots = buildDots(items.length, sectionId);
      if (oldDots) {
        controlsEl.replaceChild(newDots, oldDots);
      }
    }

    /* Re-init slideshow component */
    var slideshowEl = section.querySelector('slideshow-component');
    if (slideshowEl) {
      /* 1. Re-query slide elements (SliderComponent.resetPages) */
      if (typeof slideshowEl.resetPages === 'function') {
        slideshowEl.resetPages();
      }

      /* 2. Re-bind dot click listeners */
      var controlWrapper = slideshowEl.querySelector('.slider-buttons');
      if (controlWrapper) {
        var newLinks = Array.from(controlWrapper.querySelectorAll('.slider-counter__link'));
        slideshowEl.sliderControlLinksArray = newLinks;
        newLinks.forEach(function (link) {
          link.addEventListener('click', slideshowEl.linkToSlide.bind(slideshowEl));
        });
      }

      /* 3. Update first-item reference and current page */
      slideshowEl.sliderFirstItemNode = slideshowEl.slider
        ? slideshowEl.slider.querySelector('.slideshow__slide')
        : null;
      slideshowEl.currentPage = 1;

      /* 4. Reset scroll to first slide */
      if (slideshowEl.slider) {
        slideshowEl.slider.scrollLeft = 0;
      }

      /* 5. Re-set slide visibility */
      if (typeof slideshowEl.setSlideVisibility === 'function') {
        slideshowEl.setSlideVisibility();
      }

      /* 6. Restart autoplay if enabled */
      if (slideshowEl.slider && slideshowEl.slider.getAttribute('data-autoplay') === 'true') {
        if (typeof slideshowEl.pause === 'function') slideshowEl.pause();
        slideshowEl.autoplaySpeed = (parseInt(slideshowEl.slider.dataset.speed, 10) || 5) * 1000;
        if (typeof slideshowEl.play === 'function') slideshowEl.play();
      }
    }
  }

  /* ── Main init ──────────────────────────────────────────────── */

  function init() {
    var section = document.querySelector('[data-reviews-section]');
    if (!section) return;

    var productHandle = section.getAttribute('data-product-handle') || '';
    var occasion = getOccasion();

    /* Only run on product pages (where product handle is set) */
    if (!productHandle) return;

    if (typeof window.ProductImages === 'undefined' || typeof window.ProductImages.loadMediaMap !== 'function') {
      return;
    }

    window.ProductImages.loadMediaMap().then(function (map) {
      var reviews = resolveReviews(map, productHandle, occasion);
      if (reviews) {
        rebuildReviews(section, reviews);
      }
    }).catch(function () {
      /* On error, leave the Liquid-rendered fallback in place */
    });
  }

  document.addEventListener('DOMContentLoaded', init);
})();
